// ════════════════════════════════════════════════════════════════════════
//  QR-Code als Vektor ins PDF
//
//  Kein Bild: die Module werden als Rechtecke gezeichnet. Damit ist der Code
//  bei 20 mm so scharf wie bei 40 mm, es gibt keine Auflösungsfrage, und die
//  Matrix bleibt prüfbar – ein eingebettetes PNG wäre im Test nur eine
//  Bytelänge.
//
//  Gezeichnet wird EINMAL in ein eigenes Dokument und dann als Form eingebettet.
//  Auf allen Kärtchen einer Klasse steht derselbe QR (er trägt die Adresse, nicht
//  den Übungscode) – 25-mal dieselben ~700 Rechtecke in den Seiteninhalt zu
//  schreiben, macht aus einem Kärtchenblatt eine Datei von einem Megabyte.
// ════════════════════════════════════════════════════════════════════════

import QRCode from "qrcode";
import { PDFDocument, rgb, type PDFEmbeddedPage, type RGB } from "pdf-lib";

/**
 * Ruhezone in Modulen. Vier sind vorgeschrieben, und sie sind kein Zierrand:
 * ohne sie findet ein Scanner die Begrenzung des Codes nicht. Sie liegt INNEN,
 * die angegebene Kantenlänge gilt also für den fertigen Code samt Rand.
 */
const QUIET = 4;

export type QrRect = {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
};

/**
 * Die dunklen Module als Rechtecke, in PDF-Koordinaten (Nullpunkt unten links)
 * innerhalb eines Quadrats der Kantenlänge `size`.
 *
 * Waagerecht benachbarte Module werden zu einem Rechteck zusammengefasst. Das
 * ist keine Mikro-Optimierung: aus rund 700 Einzelmodulen werden so gut 200
 * Rechtecke, und der Inhaltsstrom des PDFs schrumpft entsprechend.
 *
 * Leerer Text ergibt eine leere Liste statt eines Wurfs – ob es einen QR gibt,
 * entscheidet der Aufrufer, nicht diese Funktion.
 */
export function qrRects(text: string, size: number): QrRect[] {
  if (text.trim() === "") return [];

  const matrix = QRCode.create(text, { errorCorrectionLevel: "M" }).modules;
  const count = matrix.size + 2 * QUIET;
  const unit = size / count;
  const rects: QrRect[] = [];

  for (let row = 0; row < matrix.size; row++) {
    let run = 0;
    for (let column = 0; column <= matrix.size; column++) {
      const dark = column < matrix.size && matrix.data[row * matrix.size + column] === 1;
      if (dark) {
        run++;
        continue;
      }
      if (run > 0) {
        rects.push({
          x: (QUIET + column - run) * unit,
          // Zeile 0 der Matrix ist oben, y wächst im PDF nach oben.
          y: size - (QUIET + row + 1) * unit,
          width: run * unit,
          height: unit,
        });
        run = 0;
      }
    }
  }

  return rects;
}

/**
 * Der fertige QR als einbettbare Form. `null`, wenn keine Adresse vorliegt –
 * dann bleibt das Kärtchen, wie es war.
 */
export async function embedQr(
  doc: PDFDocument,
  options: { readonly text: string; readonly size: number; readonly color?: RGB },
): Promise<PDFEmbeddedPage | null> {
  const rects = qrRects(options.text, options.size);
  if (rects.length === 0) return null;

  const carrier = await PDFDocument.create();
  const page = carrier.addPage([options.size, options.size]);
  const color = options.color ?? rgb(0, 0, 0);
  for (const rect of rects) page.drawRectangle({ ...rect, color });

  return doc.embedPage(page);
}
